import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider.js';
import type { TechTimeGradeDto } from '../api/techTime.js';
import { ApiError } from '../api/client.js';
import { QUERY_KEY, describeError } from './shared.js';
import { effectiveJarTarget } from './jarTarget.js';
import { HistoryModal } from './HistoryModal.js';
import styles from './Rewards.module.css';

/**
 * Live "Rewards" page (a tab in the classroom nav, gated to the owner's login).
 * Grades run across the top, selecting one shows its teacher names, tapping a
 * name opens that class's reward jar — a glass tube filled with Typing +
 * Behavior points. Adding/renaming/deleting grades + teachers lives in
 * Settings → Rewards.
 */
export function RewardsPage() {
  const api = useApi();
  const queryClient = useQueryClient();

  const gradesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.techTime.listGrades(),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });

  const [selectedGradeId, setSelectedGradeId] = useState<string | null>(null);
  const [openClassId, setOpenClassId] = useState<string | null>(null);

  const grades = useMemo(() => gradesQuery.data ?? [], [gradesQuery.data]);
  const selectedGrade = grades.find((g) => g.id === selectedGradeId) ?? grades[0] ?? null;
  const openClass = selectedGrade?.classes.find((c) => c.id === openClassId) ?? null;

  // Progress is optimistic so tapping a key feels instant.
  const setProgress = useMutation({
    mutationFn: (v: { id: string; progress: number }) => api.techTime.setProgress(v.id, v.progress),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const prev = queryClient.getQueryData<TechTimeGradeDto[]>(QUERY_KEY);
      queryClient.setQueryData<TechTimeGradeDto[]>(QUERY_KEY, (old) =>
        (old ?? []).map((g) => ({
          ...g,
          classes: g.classes.map((c) => (c.id === v.id ? { ...c, progress: v.progress } : c)),
        })),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev);
    },
  });

  if (gradesQuery.error instanceof ApiError && gradesQuery.error.status === 403) {
    return (
      <main className={styles.page} data-testid="rewards-page">
        <div className={styles.notice}>
          <p>Rewards is only available on the account it was set up for.</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page} data-testid="rewards-page">
      {setProgress.error ? (
        <div className={styles.errorBanner} role="alert" data-testid="rewards-error">
          That didn’t go through — {describeError(setProgress.error)}
        </div>
      ) : null}

      {openClass ? (
        <div className={`${styles.card} ${styles.cardTracker}`} data-testid="rewards-card">
          <ClassTracker
            classId={openClass.id}
            gradeName={selectedGrade?.name ?? ''}
            gradeGoal={selectedGrade?.goal ?? null}
            teacherName={openClass.teacherName}
            progress={openClass.progress}
            onBack={() => setOpenClassId(null)}
            onSetProgress={(progress) => setProgress.mutate({ id: openClass.id, progress })}
          />
        </div>
      ) : (
        <>
          {gradesQuery.isLoading ? <p className={styles.loading}>Loading…</p> : null}

          {!gradesQuery.isLoading && grades.length === 0 ? (
            <div className={styles.notice} data-testid="rewards-empty">
              <p>
                No grades yet — add them in <strong>Settings → Rewards</strong>.
              </p>
            </div>
          ) : null}

          {grades.length > 0 ? (
            <div className={styles.card} data-testid="rewards-card">
              <nav className={styles.grades} aria-label="Grade levels">
                {grades.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className={g.id === selectedGrade?.id ? styles.gradeTabActive : styles.gradeTab}
                    onClick={() => {
                      setSelectedGradeId(g.id);
                      setOpenClassId(null);
                    }}
                    data-testid="rewards-grade-tab"
                  >
                    {g.name}
                  </button>
                ))}
              </nav>

              {selectedGrade ? (
                <section className={styles.expand} data-testid="rewards-expand">
                  <ul className={styles.classList} aria-label={`${selectedGrade.name} teachers`}>
                    {selectedGrade.classes.map((c) => (
                      <li key={c.id} className={styles.classItem}>
                        <button
                          type="button"
                          className={styles.classButton}
                          onClick={() => setOpenClassId(c.id)}
                          data-testid="rewards-class-name"
                        >
                          <span>{c.teacherName}</span>
                          {c.progress >=
                          effectiveJarTarget(selectedGrade.name, selectedGrade.goal) ? (
                            <span className={styles.earnedPill} aria-label="earned">
                              ★
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {selectedGrade.classes.length === 0 ? (
                    <p className={styles.hint}>No teachers yet — add them in Settings → Rewards.</p>
                  ) : null}
                </section>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </main>
  );
}

function ClassTracker(props: {
  classId: string;
  gradeName: string;
  gradeGoal: number | null;
  teacherName: string;
  progress: number;
  onBack: () => void;
  onSetProgress: (progress: number) => void;
}) {
  const { progress, onSetProgress } = props;
  const [historyOpen, setHistoryOpen] = useState(false);
  const target = effectiveJarTarget(props.gradeName, props.gradeGoal);
  const earned = progress >= target;
  const fillPct = Math.min(100, (progress / target) * 100);
  // Sparse dividers every 5 points — 30 individual ticks would be clutter.
  const tickCount = Math.floor(target / TICK_STEP);

  const addPoints = (n: number) => {
    if (progress >= target) return;
    onSetProgress(Math.min(target, progress + n));
  };

  return (
    <section className={styles.tracker} data-testid="rewards-tracker">
      <div className={styles.trackerTop}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={props.onBack}
          aria-label={`Back to ${props.gradeName}`}
        >
          ‹
        </button>
        <h2 className={styles.trackerName}>{props.teacherName}</h2>
      </div>

      <div
        className={styles.tube}
        role="img"
        aria-label={`Reward jar: ${progress} of ${target} points`}
        data-testid="rewards-tube"
        data-progress={progress}
      >
        <span
          className={styles.tubeFill}
          style={{ width: `${fillPct}%` }}
          data-testid="rewards-tube-fill"
        />
        <span className={styles.tubeTicks} aria-hidden="true">
          {Array.from({ length: tickCount - 1 }, (_, i) => (
            <span
              key={i}
              className={styles.tubeTick}
              style={{ left: `${((i + 1) / tickCount) * 100}%` }}
            />
          ))}
        </span>
        <span className={styles.tubeGloss} aria-hidden="true" />
      </div>

      {earned ? (
        <p className={styles.earnedBanner} data-testid="rewards-earned">
          🎉 Free day earned!
        </p>
      ) : (
        <p className={styles.trackerHint} data-testid="rewards-progress">
          {progress}/{target} points — tap a button below when they meet a goal
        </p>
      )}

      <div className={styles.points}>
        {[1, 2, 3, 4].map((n) => (
          <button
            key={n}
            type="button"
            className={styles.pointBtn}
            onClick={() => addPoints(n)}
            disabled={earned}
            aria-label={`Add ${n} point${n === 1 ? '' : 's'}`}
            data-testid={`rewards-add-${n}`}
          >
            +{n}
          </button>
        ))}
      </div>

      <div className={styles.trackerControls}>
        <button
          type="button"
          className={styles.undoBtn}
          onClick={() => onSetProgress(Math.max(0, progress - 1))}
          disabled={progress === 0}
          data-testid="rewards-undo"
        >
          Undo
        </button>
        <button
          type="button"
          className={styles.resetBtn}
          onClick={() => onSetProgress(0)}
          disabled={progress === 0}
          data-testid="rewards-reset"
        >
          Reset
        </button>
        <button
          type="button"
          className={styles.historyBtn}
          onClick={() => setHistoryOpen(true)}
          data-testid="rewards-history-open"
        >
          History
        </button>
      </div>

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        classId={props.classId}
        teacherName={props.teacherName}
      />
    </section>
  );
}

/** Dividers every N points on the jar. */
const TICK_STEP = 5;
